import { Injectable } from '@angular/core';
import { ApprovalTree, Program } from '@models';
import { UtilDialogService } from '@core';
import { AccountDbQueryService } from './account-db-query.service';
import { AccountDbSaveService } from './account-db-save.service';

/**
 * Promote-on-demand for Proximity Organization Trees.
 *
 * ApprovalTreeUserGroup carries per-Program-User-Group purchase orders, procurement cards and
 * ordering time frames. Those resolve at order time by (ProgramUserGroup x Tree), NOT by program,
 * so on a tree shared between Programs one administrator can silently re-route another Program's
 * purchase orders. Only a tree marked IsProgramSpecific may carry them.
 *
 * Administrators do not DECLARE that need up front — they DISCOVER it, partway through building a
 * Program. So the affordance lives wherever they hit the wall, and this service is the single
 * implementation behind every one of those places:
 *
 *   1. Setting Purchase Order / P-Card type to "Per Program User Group" — the earliest and
 *      cheapest moment, before any group settings exist
 *   2. Enabling PUG-wide collection (Collect, Organize and Place)
 *   3. The Program User Group dialog, when they try to set a group setting — the latest moment,
 *      and the fallback, since that dialog is reachable with no Program context at all
 *
 * See Docs/Program-Specific-Org-Trees.md §5a.
 *
 * NOT providedIn 'root' by accident of omission — this service MUST be listed in
 * AccountModule.providers. CoreModule supplies ConfirmationService, DialogService and
 * UtilDialogService as MODULE providers, so the lazy-loaded account module holds its own
 * instances. A root-scoped service would resolve the ROOT UtilDialogService, call confirm() on a
 * ConfirmationService that the app's <p-confirmDialog> is not subscribed to, and its dialogs would
 * silently never appear. The same split makes a root-scoped save service throw
 * "Only entities in this entityManager may be saved". ProgramRulesService is registered the same way.
 */
@Injectable()
export class OrgTreeDesignationService {
  constructor(
    private dbQueryService: AccountDbQueryService,
    private dbSaveService: AccountDbSaveService,
    private dialogService: UtilDialogService,
  ) {}

  /** The current Program owning this tree, if it is designated and owned. Undefined otherwise. */
  async getOwner(accountId: string, approvalTreeId?: string) {
    if (!approvalTreeId) return undefined;
    const owners = await this.dbQueryService.getDesignatedTreeOwners(accountId);
    return owners.find((o) => o.ApprovalTreeId === approvalTreeId);
  }

  /**
   * Guard check only — does NOT mutate and does NOT save.
   *
   * THE CALLER DOES THE MUTATION AND SAVE, using its own dbSaveService. Keeping the save with
   * whoever owns the entity avoids any dependence on which injector handed this service its
   * dependencies — the same discipline that fixed the cross-account User Management status change.
   *
   * Returns `{ ok: true }` when designation is allowed, or `{ ok: false, message }` with plain
   * text explaining what blocked it. The message is returned rather than shown so a caller inside
   * a modal can render it inline.
   */
  async checkCanDesignate(approvalTree: ApprovalTree): Promise<{ ok: boolean; message?: string }> {
    if (approvalTree.isProgramSpecific) return { ok: true };

    const blockers = await this.dbQueryService.getTreeDesignationBlockers(approvalTree.id);
    if (blockers.length === 0) return { ok: true };

    const names = blockers.flatMap((b) => b.SampleNames).join(', ');
    const total = blockers.reduce((sum, b) => sum + b.Count, 0);
    return {
      ok: false,
      message:
        `'${approvalTree.name}' is used by ${total} current Programs (${names}), so it cannot be dedicated to one. ` +
        `Retire or move all but one of them, then try again — or give this Program its own copy of the tree.`,
    };
  }

  /**
   * Ask first, then designate. For callers where the offer is an INTERRUPTION — the administrator
   * flipped a radio button and needs telling why that has a consequence. `reason` names what
   * prompted it, so the prompt reads as a consequence of what they just did.
   *
   * NOT A DEAD END when blocked. A tree serving two or more current Programs cannot be designated
   * in place, but the Program can be given its own copy instead — that path lands with
   * copy-on-cycle. The message says what has to happen and never implies the administrator is
   * simply stuck.
   *
   * Callers already inside a modal should use checkCanDesignate() and render the outcome
   * themselves — by then the explanation is on screen anyway, so a second "are you sure?" would be
   * redundant, and a ConfirmDialog stacked behind an open DynamicDialog is a layering problem
   * nobody needs.
   *
   * Returns true when the administrator agreed AND the guard passed. The CALLER still performs the
   * mutation and save — see checkCanDesignate() for why.
   */
  async offerToDesignate(approvalTree: ApprovalTree, reason: string): Promise<boolean> {
    if (approvalTree.isProgramSpecific) return true;

    const ok = await this.dialogService.askIfOk(
      'Make This a Program-Specific Organization Tree?',
      `${reason}<br/><br/>` +
        `Those settings live on each Program User Group within the Organization Tree — and most trees are ` +
        `shared by several Programs, which means a setting made for one Program would apply to all of them.` +
        `<br/><br/>Marking <b>${approvalTree.name}</b> as program-specific dedicates it to this Program, so its ` +
        `group settings belong to this Program alone.` +
        `<br/><br/>Do you want to do that now?`,
    );
    if (!ok) return false;

    const result = await this.checkCanDesignate(approvalTree);
    if (!result.ok) {
      await this.dialogService.okDialog('This Tree Serves More Than One Program', result.message ?? '');
    }
    return result.ok;
  }

  /**
   * Wording for the Program form triggers. Kept here so every entry point describes the same
   * situation the same way.
   */
  reasonForPerPugPurchaseOrders() {
    return 'You have set purchase orders to be assigned <b>per Program User Group</b>.';
  }

  reasonForPerPugPcards() {
    return 'You have set procurement cards to be assigned <b>per Program User Group</b>.';
  }

  reasonForCollection() {
    return (
      'You have set this Program to <b>collect orders for review</b> before placing them with suppliers. ' +
      'A collection batch is defined by the groups on the Organization Tree, so that roster must not change ' +
      'while the batch is being reviewed.'
    );
  }

  reasonForGroupSetting() {
    return 'Purchase orders, procurement cards and ordering time frames are set <b>per Program User Group</b>.';
  }
}
